import React, { type MouseEvent, useCallback, useRef, useState, type ReactNode } from 'react';
import { cssMap } from '@compiled/react';
import noop from 'lodash/noop';
import { IconButton } from '@atlaskit/button/new';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import MoreIcon from '@atlaskit/icon/core/show-more-horizontal';
import { Flex } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import type { UIAnalyticsEvent as AnalyticsEvent } from '@atlaskit/analytics-next';
import ComponentWithAnalytics from '@atlassian/jira-analytics-web-react/src/utils/component-with-analytics.tsx';
import type { User } from '@atlassian/jira-common-directory-v2/src/model/index.tsx';
import type { ReturnFocusTo } from '@atlassian/jira-software-modal-dialog/src/common/types.tsx';
import { type IntlShape, useIntl } from '@atlassian/jira-intl';
import { mergeRefs } from '@atlassian/jira-merge-refs/src/index.tsx';
import { useModalDialogActions } from '@atlassian/jira-software-modal-dialog/src/controllers/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import messages from './messages.tsx';

const DropdownMenuWithAnalytics = ComponentWithAnalytics('dropdownMenu', {
	onOpenChange: 'open',
})(DropdownMenu);

const DropdownItemWithAnalytics = ComponentWithAnalytics('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

type Props = {
	isWritable: boolean;
	systemDashboard: boolean;
	defaultOpen: boolean;
	intl: IntlShape;
	showSpinner: boolean;
	loggedInUserAccountId: string;
	caption: string;
	owner: User | null;
	onCopy: () => void;
	onEdit: () => void;
	onChangeOwner: () => void;
	onDelete: () => void;
	onCopyDashboardClick: (arg1: AnalyticsEvent) => void;
	onEditDashboardClick: (arg1: AnalyticsEvent) => void;
	onChangeOwnerClick: (arg1: AnalyticsEvent) => void;
	onDeleteDashboardClick: (arg1: AnalyticsEvent) => void;
	setReturnFocusTo: (returnFocusTo: ReturnFocusTo | null) => void | Promise<void>;
};

type State = {
	isOpen: boolean;
};

export default function Actions(props: Props): React.JSX.Element {
	const {
		isWritable = false,
		systemDashboard = false,
		defaultOpen = false,
		showSpinner = false,
		loggedInUserAccountId = '',
		owner = null,
		caption = '',
		onChangeOwner = noop,
		onCopy = noop,
		onEdit = noop,
		onDelete = noop,
		onCopyDashboardClick = noop,
		onEditDashboardClick = noop,
		onChangeOwnerClick = noop,
		onDeleteDashboardClick = noop,
	} = props;

	const { formatMessage } = useIntl();

	const { setReturnFocusTo } = useModalDialogActions();

	const [isDropdownOpen, setIsDropdownOpen] = useState(defaultOpen);

	const triggerButtonRef = useRef<HTMLButtonElement>(null);

	const onOpenChange = useCallback(({ isOpen }: State) => {
		setIsDropdownOpen(isOpen);
	}, []);

	// /**
	//  * Close the menu with a minimal delay so the inner handlers are able to react on keypress properly.
	//  * In its current implementation if we close the menu right away it won't open the focused `href`.
	//  * Ideally we have to get right of this and few more methods when the bug linked below is fixed.
	//  * @see https://ecosystem.atlassian.net/browse/AK-4467
	//  */
	// const delayedClose = useCallback(() => {
	// 	setTimeout(() => setIsDropdownOpen(false));
	// }, []);

	const handleCopy = useCallback(
		(e: MouseEvent<Element> | React.KeyboardEvent<Element>, analytics: AnalyticsEvent) => {
			onCopyDashboardClick(analytics);
			onCopy();
			if (fg('jfp_a11y_fix_dashboard_actions_modal_accessibility')) {
				triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
			}
		},
		[setReturnFocusTo, onCopy, onCopyDashboardClick],
	);

	const handleEdit = useCallback(
		(e: MouseEvent<Element> | React.KeyboardEvent<Element>, analytics: AnalyticsEvent) => {
			onEditDashboardClick(analytics);
			onEdit();
			if (fg('jfp_a11y_fix_dashboard_actions_modal_accessibility')) {
				triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
			}
		},
		[setReturnFocusTo, onEdit, onEditDashboardClick],
	);

	const handleChangeOwner = useCallback(
		(e: MouseEvent<Element> | React.KeyboardEvent<Element>, analytics: AnalyticsEvent) => {
			onChangeOwnerClick(analytics);
			onChangeOwner();

			if (fg('jfp_a11y_fix_dashboard_actions_modal_accessibility')) {
				triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
			}
		},
		[setReturnFocusTo, onChangeOwner, onChangeOwnerClick],
	);

	const handleDelete = useCallback(
		(e: MouseEvent<Element> | React.KeyboardEvent<Element>, analytics: AnalyticsEvent) => {
			onDeleteDashboardClick(analytics);
			onDelete();
			if (fg('jfp_a11y_fix_dashboard_actions_modal_accessibility')) {
				triggerButtonRef.current && setReturnFocusTo(triggerButtonRef);
			}
		},
		[setReturnFocusTo, onDelete, onDeleteDashboardClick],
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const triggerFunction = ({ triggerRef, ...buttonProps }: { triggerRef: React.Ref<any> }) => (
		<IconButton
			{...buttonProps}
			testId="dashboards-main.content.cells.actions.button"
			appearance="subtle"
			icon={MoreIcon}
			spacing="compact"
			ref={mergeRefs(triggerRef, triggerButtonRef)}
			label={formatMessage(messages.moreActions, {
				dashboardName: caption,
			})}
			isLoading={showSpinner}
		/>
	);

	const renderEditAction = () => (
		<DropdownItemWithAnalytics onClick={handleEdit}>
			{formatMessage(systemDashboard ? messages.shareAction : messages.renameOrShareAction)}
		</DropdownItemWithAnalytics>
	);

	const renderCopyAction = () => (
		<DropdownItemWithAnalytics onClick={handleCopy}>
			{formatMessage(messages.copyAction)}
		</DropdownItemWithAnalytics>
	);

	const renderChangeOwnerAction = () => (
		<DropdownItemWithAnalytics onClick={handleChangeOwner}>
			{formatMessage(messages.changeOwner)}
		</DropdownItemWithAnalytics>
	);

	const renderDeleteAction = () => (
		<DropdownItemWithAnalytics onClick={handleDelete}>
			{formatMessage(messages.moveToTrashAction)}
		</DropdownItemWithAnalytics>
	);

	const isOwnedByCurrentUser = owner && owner.accountId === loggedInUserAccountId;

	const copyItem = renderCopyAction();
	let editItem = null;
	let changeOwnerItem = null;
	let deleteItem = null;

	if (isWritable) {
		editItem = renderEditAction();
	}

	if (isOwnedByCurrentUser) {
		changeOwnerItem = renderChangeOwnerAction();
		deleteItem = renderDeleteAction();
	}

	return (
		<Container>
			<DropdownMenuWithAnalytics
				placement="bottom-end"
				isOpen={isDropdownOpen}
				onOpenChange={onOpenChange}
				trigger={triggerFunction}
				shouldRenderToParent
			>
				<DropdownItemGroup>
					{copyItem}
					{editItem}
					{changeOwnerItem}
					{deleteItem}
				</DropdownItemGroup>
			</DropdownMenuWithAnalytics>
		</Container>
	);
}

const Container = ({ children }: { children: ReactNode }) => (
	<Flex xcss={styles.container}>{children}</Flex>
);

const styles = cssMap({
	container: {
		paddingLeft: token('space.100'),
		paddingBlock: token('space.100'),
		paddingRight: token('space.150'),
	},
});
